<template>
  <!--
    `defer` is load-bearing, not tidiness. `ion-app` is rendered by Vue in App.vue and
    is NOT in public/index.html, so on a COLD load straight to a route this component
    can mount before the target exists. Vue then silently mounts no teleport content,
    `$refs.ionModal` stays undefined, and the first open throws
    `Cannot read properties of undefined (reading '$el')` — from a watcher, so the
    modal simply never appears and the page looks fine.
    That is how the voucher filter sheet was unreachable for anyone arriving at
    /vouchers by deep link or QR, while working perfectly if you tapped through from
    the Me page. `defer` moves the target lookup to after the render cycle that
    creates `ion-app`.
  -->
  <Teleport defer to="ion-app">
    <ion-modal
      ref="ionModal"
      :can-dismiss="true"
      :backdrop-dismiss="true"
      :show-backdrop="true"
      :initial-breakpoint="1"
      :breakpoints="[0, 1]"
      :backdrop-breakpoint="0.5"
      @ionModalDidDismiss="onDismiss"
      @ionModalWillDismiss="onWillDismiss"
      class="bottom-sheet-modal"
      :class="toneClasses.modal"
    >
      <div class="sheet-container" @click="close">
        <div
          class="sheet-content"
          :class="[safeAreaClass, toneClasses.content, { 'remove-space': removeSpace }]"
          @click.stop
        >
          <!-- Content -->
          <slot></slot>
        </div>
      </div>
    </ion-modal>
  </Teleport>
</template>

<script>
import { IonModal } from '@ionic/vue';
import { isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';

export default {
  name: 'ModalSheet',
  components: {
    IonModal,
  },
  props: {
    // `campaign` draws the sheet in the design system's campaign palette
    // (--hogc-campaign-*), for the BF2026 pledge's explainer sheets.
    tone: {
      type: String,
      default: 'default',
      validator: value => ['default', 'campaign'].includes(value),
    },
    modelValue: {
      type: Boolean,
      required: true,
    },
    removeSpace: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    // The campaign tone (the pledge frames' T and P1c sheets). Important, as the
    // scoped defaults below outrank a utility class.
    toneClasses() {
      if (this.tone !== 'campaign') return { modal: '', content: '' };
      return {
        modal: '[&::part(content)]:!bg-campaign-surface [&::part(handle)]:!bg-campaign-line',
        content: '!bg-campaign-surface text-campaign-ink',
      };
    },

    safeAreaClass() {
      if (isIosNativeOnly()) {
        return 'ios-safe-area';
      }
      if (isAndroidNativeOnly()) {
        return 'android-safe-area';
      }
      return '';
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.$refs.ionModal?.$el?.present();
      } else {
        this.$refs.ionModal?.$el?.dismiss();
      }
    },
  },
  mounted() {
    // nextTick because of `defer` above: the teleport content is mounted after this
    // render cycle, so the ref does not exist yet when a modal starts open.
    if (this.modelValue) {
      this.$nextTick(() => this.$refs.ionModal?.$el?.present());
    }
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false);
    },
    onDismiss() {
      this.$emit('update:modelValue', false);
    },
    onWillDismiss() {},
  },
};
</script>
<style scoped>
.bottom-sheet-modal {
  --width: 100%;
  --height: auto;
  --max-width: 100%;
  --max-height: 638px;
  --background: transparent;
  --backdrop-opacity: 0.5;
  --border-radius: 24px 24px 0 0;
}

.bottom-sheet-modal::part(backdrop) {
  background-color: rgba(0, 0, 0, 0.5);
}

.bottom-sheet-modal::part(content) {
  background: #f6f4f0;
  border-radius: 24px 24px 0 0;
  max-height: 638px;
  overflow: auto;
}

/* Style Ionic's drag handle */
.bottom-sheet-modal::part(handle) {
  margin: 8px auto 8px auto;
  width: 40px;
  height: 5px;
  background-color: #d5cfc8;
  border-radius: 3px;
}

.sheet-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.sheet-content {
  background: #f6f4f0;
  border-radius: 24px 24px 0 0;
  padding: 32px 24px 24px 24px;
  max-height: 638px;
  overflow-y: auto;
  position: relative;
}

/* Safe area handling */
.ios-safe-area .sheet-content {
  padding-bottom: max(24px, env(safe-area-inset-bottom));
}

.android-safe-area .sheet-content {
  padding-bottom: max(24px, env(safe-area-inset-bottom));
}

/* removeSpace prop: remove bottom safe area padding */
.remove-space .sheet-content {
  padding-bottom: 24px !important;
  margin-bottom: 0 !important;
}

/* Responsive padding */
@media (min-width: 640px) {
  .sheet-content {
    padding: 32px 32px 32px 32px;
  }
}

@media (min-width: 1024px) {
  .sheet-content {
    padding: 32px 40px 40px 40px;
  }
}

/* Hide scrollbar for clean look */
.sheet-content::-webkit-scrollbar {
  width: 0px;
  background: transparent;
}

.sheet-content {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
